import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { Store } from '@ngxs/store';
import { forkJoin } from 'rxjs';
// @ts-ignore: CropperJS types may be incomplete
import Cropper from 'cropperjs';

import { CourseLevelEnum, CourseLevelEnumDescriptions } from '../../../models/enums/courseLevelEnum';
import { Gender, GenderDescriptions } from '../../../models/enums/enums';
import { Prediction } from '../../../models/country/olaPlacesResponse';
import { UtilityService } from '../../../services/utility/utility.service';
import { AuthService } from '../../../services/account/auth.service';
import { AccountCentreService } from '../../../account-centre/account-centre.service';
import { UpdateProfileCoverImage } from '../../../states/user/user-action';

@Component({
  selector: 'app-edit-personal-details',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    CalendarModule,
    DropdownModule,
    MultiSelectModule,
    AutoCompleteModule,
    ButtonModule,
    DialogModule,
    ToastModule
  ],
  providers: [MessageService],
  templateUrl: './edit-personal-details.component.html',
  styleUrls: ['./edit-personal-details.component.scss']
})
export class EditPersonalDetailsComponent implements OnInit {
  @Input() userId = '';
  @Output() saved = new EventEmitter<void>();
  @Output() photoUpdated = new EventEmitter<string>();
  @Output() close = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  maxDob = new Date();

  // ─── Profile photo / cropper ────────────────────────────────────────────────
  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('imageToCrop') imageToCropRef!: ElementRef<HTMLImageElement>;
  currentPhoto: string | null = null;
  croppingImageUrl: string | null = null;
  uploadingPhoto = false;
  private cropper: Cropper | null = null;

  firstName = '';
  lastName = '';

  private suggestionsRaw: Prediction[] = [];
  suggestions: Prediction[] = [];
  private selectedLatitude: number | null = null;
  private selectedLongitude: number | null = null;

  readonly genders = [
    { name: GenderDescriptions[Gender.Male], value: Gender.Male },
    { name: GenderDescriptions[Gender.Female], value: Gender.Female },
    { name: GenderDescriptions[Gender.Other], value: Gender.Other }
  ];

  readonly courseLevels = [
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.BachelorsDegree], key: CourseLevelEnum.BachelorsDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.MastersDegree], key: CourseLevelEnum.MastersDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.DoctoralDegree], key: CourseLevelEnum.DoctoralDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.Diploma], key: CourseLevelEnum.Diploma },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.Certificate], key: CourseLevelEnum.Certificate }
  ];

  readonly countries = [
    { label: 'India', value: 'IN', icon: 'assets/icons/india-flag.svg' },
    { label: 'UK', value: 'GB', icon: 'assets/icons/uk.svg' },
    { label: 'Canada', value: 'CA', icon: 'assets/icons/canada.svg' },
    { label: 'USA', value: 'US', icon: 'assets/icons/usa.svg' },
    { label: 'Other', value: 'Other', icon: 'assets/icons/other.png' }
  ];

  readonly journeyStatuses = [
    { name: 'An Early Explorer', key: 'An Early Explorer', icon: 'assets/icons/telescope.png' },
    { name: 'Finalising Shortlist', key: 'Finalising Shortlist', icon: 'assets/icons/target.png' },
    { name: 'Ready-to-Apply', key: 'Ready-to-Apply', icon: 'assets/icons/touch-screen.png' }
  ];

  collegeStartTimings: { name: string; key: string }[] = [];

  private readonly levelsNeedingParentInfo = [
    CourseLevelEnum.BachelorsDegree,
    CourseLevelEnum.BachelorsAndMastersDegree,
    CourseLevelEnum.Certificate,
    CourseLevelEnum.Diploma,
    CourseLevelEnum.FoundationDegree,
    CourseLevelEnum.UndergraduateCertificate,
    CourseLevelEnum.UndergraduateDiploma,
    CourseLevelEnum.Vocational
  ];

  constructor(
    private fb: FormBuilder,
    private utilityService: UtilityService,
    private authService: AuthService,
    private accountCentreService: AccountCentreService,
    private messageService: MessageService,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      firstName: ['', Validators.required],
      lastName: [''],
      gender: [null, Validators.required],
      dateOfBirth: [null, Validators.required],
      location: ['', Validators.required],
      intendedCountries: [[], Validators.required],
      intendedLevel: [null, Validators.required],
      collegeStartTiming: [null, Validators.required],
      journeyStatus: [null, Validators.required]
    });
    this.setCollegeStartTimings();
    this.prefill();
  }

  get initials(): string {
    const f = this.firstName?.[0] ?? '';
    const l = this.lastName?.[0] ?? '';
    return ((f + l).toUpperCase()) || (this.firstName?.substring(0, 2).toUpperCase() ?? '') || 'GP';
  }

  private prefill(): void {
    if (!this.userId) return;
    this.authService.getUserByid(this.userId).subscribe({
      next: user => {
        this.firstName = user.firstName || '';
        this.lastName = user.lastName || '';
        this.currentPhoto = user.imageUrl || null;
        this.form.patchValue({
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          gender: user.gender || null,
          dateOfBirth: user.bornOn ? new Date(user.bornOn) : null,
          location: user.location || '',
          intendedCountries: user.destination || [],
          intendedLevel:
            user.degreePreference !== null && user.degreePreference !== undefined ? user.degreePreference : null,
          collegeStartTiming: user.collageStartOn || null,
          journeyStatus: user.journeyStatus || null
        });
        this.selectedLatitude = user.latitude ?? null;
        this.selectedLongitude = user.longitude ?? null;
      }
    });
  }

  private setCollegeStartTimings(): void {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    this.collegeStartTimings = [];
    const maxYear = currentYear + 4;

    for (let year = currentYear; year <= maxYear; year++) {
      const springDeadlineYear = year - 1;
      const springPassed = currentYear > springDeadlineYear || (currentYear === springDeadlineYear && currentMonth > 10);
      if (!springPassed) this.collegeStartTimings.push({ name: `Spring ${year}`, key: `Spring ${year}` });

      const fallPassed = currentYear > year || (currentYear === year && currentMonth > 3);
      if (!fallPassed) this.collegeStartTimings.push({ name: `Fall ${year}`, key: `Fall ${year}` });
    }
  }

  // ─── Location autocomplete ──────────────────────────────────────────────────
  searchLocation(event: { query: string }): void {
    this.utilityService.getOLAPlaces(event.query).subscribe({
      next: res => {
        this.suggestionsRaw = res?.predictions ?? [];
        this.suggestions = this.suggestionsRaw;
      },
      error: () => { this.suggestions = []; }
    });
  }

  onLocationSelect(event: { value: Prediction }): void {
    const loc = event?.value?.geometry?.location;
    this.selectedLatitude = loc?.lat ?? null;
    this.selectedLongitude = loc?.lng ?? null;
  }

  // ─── Profile photo: pick → crop → upload ────────────────────────────────────
  openPhotoPicker(): void {
    this.fileInputRef?.nativeElement.click();
  }

  onPhotoSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      this.messageService.add({ severity: 'error', summary: 'File too large', detail: 'Please choose an image under 5MB.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.croppingImageUrl = reader.result as string;
      setTimeout(() => this.initCropper(), 100);
    };
    reader.readAsDataURL(file);
    // allow re-selecting the same file later
    (event.target as HTMLInputElement).value = '';
  }

  private initCropper(): void {
    if (this.cropper) { this.cropper.destroy(); this.cropper = null; }
    if (!this.imageToCropRef) return;
    this.cropper = new Cropper(this.imageToCropRef.nativeElement, {
      aspectRatio: 1,
      viewMode: 1,
      autoCropArea: 1,
      responsive: true,
      background: false
    } as any);
  }

  cancelCrop(): void {
    if (this.cropper) { this.cropper.destroy(); this.cropper = null; }
    this.croppingImageUrl = null;
  }

  cropAndUpload(): void {
    if (!this.cropper) return;
    const canvas = this.cropper.getCroppedCanvas({ width: 512, height: 512 });
    const base64 = canvas.toDataURL('image/png');
    const file = this.dataURLtoFile(base64, 'profile.png');

    this.uploadingPhoto = true;
    this.accountCentreService.updatePhoto({ profilePhoto: file, coverPhoto: null }).subscribe({
      next: (response: any) => {
        this.uploadingPhoto = false;
        const newUrl = response?.profileImageUrl ?? base64;
        this.currentPhoto = newUrl;
        this.store.dispatch(new UpdateProfileCoverImage({
          profileImageUrl: response?.profileImageUrl ?? undefined,
          profileImageUrlFormat: response?.profileImageFormat ?? undefined
        }));
        this.photoUpdated.emit(newUrl);
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Profile photo updated.' });
        this.cancelCrop();
      },
      error: () => {
        this.uploadingPhoto = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to upload photo. Please try again.' });
      }
    });
  }

  private dataURLtoFile(dataUrl: string, filename: string): File {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
  }

  // ─── Save personal details ──────────────────────────────────────────────────
  isInvalid(name: string): boolean {
    const c = this.form.get(name);
    return !!c && c.invalid && (c.touched || c.dirty);
  }

  reset(): void {
    this.form.reset();
    this.prefill();
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading = true;
    const v = this.form.value;
    const selectedLevel = v.intendedLevel;

    const loc = v.location;
    const locationDescription = typeof loc === 'string' ? loc : (loc?.description || null);
    if (loc && typeof loc === 'object' && loc?.geometry?.location) {
      this.selectedLatitude = loc.geometry.location.lat ?? null;
      this.selectedLongitude = loc.geometry.location.lng ?? null;
    }

    const dob: Date | null = v.dateOfBirth;
    const dateOfBirth = dob instanceof Date
      ? `${dob.getFullYear()}-${String(dob.getMonth() + 1).padStart(2, '0')}-${String(dob.getDate()).padStart(2, '0')}`
      : null;

    const personBody: Record<string, unknown> = {
      firstName: v.firstName,
      lastName: v.lastName,
      gender: v.gender,
      dateOfBirth
    };

    const studentBody: Record<string, unknown> = {
      destination: v.intendedCountries,
      degreePreference:
        selectedLevel === null || selectedLevel === undefined || selectedLevel === '' ? null : Number(selectedLevel),
      collageStartOn: v.collegeStartTiming,
      journeyStatus: v.journeyStatus,
      location: locationDescription,
      latitude: this.selectedLatitude,
      longitude: this.selectedLongitude
    };

    forkJoin({
      person: this.accountCentreService.updateMyPerson(personBody),
      student: this.accountCentreService.updateMyStudentProfile(studentBody)
    }).subscribe({
      next: () => {
        this.loading = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Personal details updated.' });
        this.saved.emit();
      },
      error: (err) => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.message || 'Failed to update personal details.'
        });
      }
    });
  }
}
